import type { PropsWithChildren } from 'react'
import type { Toast, ToastInput } from './types'
import { useCallback, useEffect, useRef, useState } from 'react'
import { ToasterContext } from './ToasterContext'
import { ToasterHost } from './ToasterHost'

const DEFAULT_DURATION = 5000

interface ToasterProviderProps extends PropsWithChildren {
  defaultDuration?: number | null
}

export function ToasterProvider({ children, defaultDuration = DEFAULT_DURATION }: Readonly<ToasterProviderProps>) {
  const [toasts, setToasts] = useState<Toast[]>([])
  const timersRef = useRef(new Map<string, ReturnType<typeof setTimeout>>())
  const nextIdRef = useRef(0)

  const dismissToast = useCallback((id: string) => {
    const timer = timersRef.current.get(id)

    if (timer) {
      clearTimeout(timer)
      timersRef.current.delete(id)
    }

    setToasts(currentToasts => currentToasts.filter(toast => toast.id !== id))
  }, [])

  const showToast = useCallback((input: ToastInput) => {
    const id = `toast-${++nextIdRef.current}`
    const toast: Toast = {
      ...input,
      id,
      duration: input.duration === undefined ? defaultDuration : input.duration,
      dismissible: input.dismissible ?? true,
    }

    setToasts(currentToasts => [...currentToasts, toast])

    if (toast.duration !== null && toast.duration > 0) {
      timersRef.current.set(id, setTimeout(dismissToast, toast.duration, id))
    }

    return id
  }, [defaultDuration, dismissToast])

  const clearToast = useCallback(() => {
    timersRef.current.forEach(timer => clearTimeout(timer))
    timersRef.current.clear()
    setToasts([])
  }, [])

  useEffect(() => () => {
    timersRef.current.forEach(timer => clearTimeout(timer))
    timersRef.current.clear()
  }, [])

  return (
    <ToasterContext value={{ toasts, showToast, dismissToast, clearToast }}>
      {children}
      <ToasterHost toasts={toasts} onDismiss={dismissToast} />
    </ToasterContext>
  )
}
